import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { MarkdownPreview } from './MarkdownPreview';

/** Debounce delay for saving content to server (milliseconds) */
const SAVE_DEBOUNCE_MS = 500;

/**
 * Creates a debounced version of a function.
 * The function will only execute after the specified delay has passed
 * without any new calls.
 */
function useDebouncedCallback<T extends (...args: Parameters<T>) => void>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useMemo(() => {
    const debouncedFn = (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    };
    return debouncedFn as T;
  }, [delay]);
}

interface SplitPaneEditorProps {
  initialContent: string;
  onChange: (content: string) => void;
  onEscape: () => void;
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  onShiftReturn?: () => void;
}

type ViewMode = 'split' | 'edit' | 'preview';

type FormatType = 'bold' | 'italic' | 'heading' | 'link' | 'list' | 'code' | 'codeBlock' | 'quote' | 'table';

interface FormatConfig {
  prefix: string;
  suffix: string;
  placeholder: string;
  blockLevel?: boolean;
}

const FORMAT_CONFIGS: Record<FormatType, FormatConfig> = {
  bold: { prefix: '**', suffix: '**', placeholder: 'bold text' },
  italic: { prefix: '_', suffix: '_', placeholder: 'italic text' },
  heading: { prefix: '## ', suffix: '', placeholder: 'Heading', blockLevel: true },
  link: { prefix: '[', suffix: '](url)', placeholder: 'link text' },
  list: { prefix: '- ', suffix: '', placeholder: 'list item', blockLevel: true },
  code: { prefix: '`', suffix: '`', placeholder: 'code' },
  codeBlock: { prefix: '```\n', suffix: '\n```', placeholder: 'code here', blockLevel: true },
  quote: { prefix: '> ', suffix: '', placeholder: 'quote', blockLevel: true },
  table: { prefix: '| Header 1 | Header 2 |\n| -------- | -------- |\n| ', suffix: ' | cell 2 |', placeholder: 'cell 1', blockLevel: true },
};

export function SplitPaneEditor({
  initialContent,
  onChange,
  onEscape,
  onNavigateNext,
  onNavigatePrev,
  onShiftReturn,
}: SplitPaneEditorProps) {
  const [content, setContent] = useState(initialContent);
  const [viewMode, setViewMode] = useState<ViewMode>('split');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Debounce the onChange callback to avoid excessive API calls
  const debouncedOnChange = useDebouncedCallback(onChange, SAVE_DEBOUNCE_MS);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
      // Move cursor to end
      textareaRef.current.selectionStart = textareaRef.current.value.length;
      textareaRef.current.selectionEnd = textareaRef.current.value.length;
    }
  }, []);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    // Debounced save to server - immediate local state update for responsive UI
    debouncedOnChange(newContent);
  }, [debouncedOnChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Stop propagation for all arrow keys to prevent drag handler interference
    if (e.key.startsWith('Arrow')) {
      e.stopPropagation();
    }

    // Escape - exit edit mode
    if (e.key === 'Escape') {
      e.preventDefault();
      onEscape();
      return;
    }

    // Shift+Return - finish and move to next
    if (e.key === 'Enter' && e.shiftKey) {
      e.preventDefault();
      onShiftReturn?.();
      return;
    }

    // Arrow navigation at boundaries
    if (e.key === 'ArrowDown' && onNavigateNext) {
      const { selectionStart, value } = textarea;
      const lines = value.split('\n');
      let charCount = 0;
      let currentLine = 0;

      // Find current line
      for (let i = 0; i < lines.length; i++) {
        if (charCount + lines[i].length >= selectionStart) {
          currentLine = i;
          break;
        }
        charCount += lines[i].length + 1; // +1 for newline
      }

      // If on last line, navigate to next section
      if (currentLine === lines.length - 1) {
        e.preventDefault();
        const cursorXPosition = selectionStart - charCount;
        onNavigateNext();
        // Store cursor position for next section (via custom event or context)
        window.dispatchEvent(new CustomEvent('section-cursor-position', {
          detail: { x: cursorXPosition, fromBottom: true }
        }));
      }
    }

    if (e.key === 'ArrowUp' && onNavigatePrev) {
      const { selectionStart, value } = textarea;
      const lines = value.split('\n');
      let charCount = 0;
      let currentLine = 0;

      // Find current line
      for (let i = 0; i < lines.length; i++) {
        if (charCount + lines[i].length >= selectionStart) {
          currentLine = i;
          break;
        }
        charCount += lines[i].length + 1;
      }

      // If on first line, navigate to previous section
      if (currentLine === 0) {
        e.preventDefault();
        const cursorXPosition = selectionStart;
        onNavigatePrev();
        window.dispatchEvent(new CustomEvent('section-cursor-position', {
          detail: { x: cursorXPosition, fromBottom: false }
        }));
      }
    }
  }, [onEscape, onNavigateNext, onNavigatePrev, onShiftReturn]);

  // Apply markdown formatting at cursor or around selection
  const applyFormat = useCallback((formatType: FormatType) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { prefix, suffix, placeholder, blockLevel } = FORMAT_CONFIGS[formatType];
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const textToWrap = selectedText || placeholder;

    let newContent: string;
    let newCursorStart: number;
    let newCursorEnd: number;

    if (blockLevel && start > 0 && content[start - 1] !== '\n') {
      // For block-level elements, ensure we're on a new line
      newContent = content.substring(0, start) + '\n' + prefix + textToWrap + suffix + content.substring(end);
      newCursorStart = start + 1 + prefix.length;
      newCursorEnd = newCursorStart + textToWrap.length;
    } else {
      newContent = content.substring(0, start) + prefix + textToWrap + suffix + content.substring(end);
      newCursorStart = start + prefix.length;
      newCursorEnd = newCursorStart + textToWrap.length;
    }

    setContent(newContent);
    debouncedOnChange(newContent);

    // Restore focus and selection
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = newCursorStart;
      textarea.selectionEnd = newCursorEnd;
    });
  }, [content, debouncedOnChange]);

  // Listen for cursor position from other sections
  useEffect(() => {
    const handleCursorPosition = (e: Event) => {
      const { x, fromBottom } = (e as CustomEvent).detail;
      const textarea = textareaRef.current;
      if (!textarea) return;

      const lines = content.split('\n');
      let targetPos: number;

      if (fromBottom) {
        // Coming from above, position at first line
        const firstLineLength = lines[0]?.length || 0;
        targetPos = Math.min(x, firstLineLength);
      } else {
        // Coming from below, position at last line
        const totalLength = content.length;
        const lastLineStart = totalLength - (lines[lines.length - 1]?.length || 0);
        const lastLineLength = lines[lines.length - 1]?.length || 0;
        targetPos = lastLineStart + Math.min(x, lastLineLength);
      }

      textarea.focus();
      textarea.selectionStart = targetPos;
      textarea.selectionEnd = targetPos;
    };

    window.addEventListener('section-cursor-position', handleCursorPosition);
    return () => window.removeEventListener('section-cursor-position', handleCursorPosition);
  }, [content]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '4px',
          padding: '8px 12px',
          borderBottom: '1px solid var(--border-default, #e2e8f0)',
          backgroundColor: 'var(--bg-subtle, #f8fafc)',
        }}
      >
        {/* Formatting buttons */}
        <div style={{ display: 'flex', gap: '2px' }}>
          <FormatButton onClick={() => applyFormat('bold')} title="Bold" label="B" bold />
          <FormatButton onClick={() => applyFormat('italic')} title="Italic" label="I" italic />
          <FormatButton onClick={() => applyFormat('heading')} title="Heading" label="H" />
          <FormatButton onClick={() => applyFormat('link')} title="Link" label="🔗" />
          <FormatButton onClick={() => applyFormat('list')} title="Bullet list" label="•" />
          <FormatButton onClick={() => applyFormat('quote')} title="Blockquote" label="❝" />
          <FormatButton onClick={() => applyFormat('code')} title="Inline code" label="`" mono />
          <FormatButton onClick={() => applyFormat('codeBlock')} title="Code block" label="```" mono />
          <FormatButton onClick={() => applyFormat('table')} title="Table" label="⊞" />
        </div>

        {/* View mode buttons */}
        <div style={{ display: 'flex', gap: '4px' }}>
          <ViewModeButton
            mode="edit"
            currentMode={viewMode}
            onClick={() => setViewMode('edit')}
            label="Edit"
          />
          <ViewModeButton
            mode="split"
            currentMode={viewMode}
            onClick={() => setViewMode('split')}
            label="Split"
          />
          <ViewModeButton
            mode="preview"
            currentMode={viewMode}
            onClick={() => setViewMode('preview')}
            label="Preview"
          />
        </div>
      </div>

      {/* Editor panes */}
      <div
        style={{
          display: 'flex',
          flex: 1,
          minHeight: '200px',
        }}
      >
        {/* Raw markdown pane */}
        {(viewMode === 'split' || viewMode === 'edit') && (
          <div
            style={{
              flex: viewMode === 'split' ? 1 : 1,
              display: 'flex',
              flexDirection: 'column',
              borderRight: viewMode === 'split' ? '1px solid var(--border-default, #e2e8f0)' : 'none',
            }}
          >
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Write markdown here..."
              style={{
                flex: 1,
                padding: '24px',
                border: 'none',
                outline: 'none',
                resize: 'none',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: '14px',
                lineHeight: 1.6,
                color: 'var(--text-primary, #1e293b)',
                backgroundColor: 'var(--bg-base, #fff)',
              }}
            />
          </div>
        )}

        {/* Preview pane */}
        {(viewMode === 'split' || viewMode === 'preview') && (
          <div
            style={{
              flex: 1,
              padding: '24px',
              overflow: 'auto',
              backgroundColor: 'var(--bg-base, #fff)',
            }}
          >
            <MarkdownPreview content={content} className="prose" />
          </div>
        )}
      </div>

      {/* Keyboard shortcuts hint */}
      <div
        style={{
          padding: '4px 12px',
          fontSize: '11px',
          color: 'var(--text-muted, #94a3b8)',
          borderTop: '1px solid var(--border-default, #e2e8f0)',
          backgroundColor: 'var(--bg-subtle, #f8fafc)',
        }}
      >
        <kbd>Esc</kbd> exit • <kbd>Shift+Return</kbd> next section • <kbd>↑</kbd>/<kbd>↓</kbd> at boundary navigates sections
      </div>
    </div>
  );
}

interface ViewModeButtonProps {
  mode: ViewMode;
  currentMode: ViewMode;
  onClick: () => void;
  label: string;
}

function ViewModeButton({ mode, currentMode, onClick, label }: ViewModeButtonProps) {
  const isActive = mode === currentMode;
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 12px',
        fontSize: '12px',
        fontWeight: isActive ? 600 : 400,
        // Active: dark text on light bg (works in both modes)
        // Inactive: semantic text color on transparent
        color: isActive ? 'var(--ceetrix-800)' : 'var(--text-secondary)',
        backgroundColor: isActive ? 'var(--ceetrix-50)' : 'transparent',
        border: '1px solid var(--border-default)',
        borderRadius: '4px',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

interface FormatButtonProps {
  onClick: () => void;
  title: string;
  label: string;
  bold?: boolean;
  italic?: boolean;
  mono?: boolean;
}

function FormatButton({ onClick, title, label, bold, italic, mono }: FormatButtonProps) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        padding: '4px 8px',
        fontSize: '13px',
        fontWeight: bold ? 700 : 400,
        fontStyle: italic ? 'italic' : 'normal',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' : 'inherit',
        color: 'var(--text-secondary)',
        backgroundColor: 'transparent',
        border: '1px solid transparent',
        borderRadius: '4px',
        cursor: 'pointer',
        minWidth: '28px',
        lineHeight: 1,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--bg-muted)';
        e.currentTarget.style.borderColor = 'var(--border-default)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.borderColor = 'transparent';
      }}
    >
      {label}
    </button>
  );
}
